import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getSortOptionFromURI } from "components/Filters/helpers";
import {
  EmptystateSearchNoResultsColored,
  EmptystateShareColored,
} from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrderDirection } from "types/generated";

import {
  SPACE_IDP_GROUPS_FILTERS_ORDER_KEY,
  SPACE_IDP_GROUPS_INITIAL_LIMIT,
  SPACE_IDP_GROUPS_INITIAL_ORDER_BY,
  SPACE_IDP_GROUPS_LIMIT_STORAGE_KEY,
  SPACE_IDP_GROUPS_SORT_CONFIG,
} from "./constants";
import SpaceDetailsLoginPolicyCallout from "../components/LoginPolicyCallout";
import SpaceDetailsIdpGroupsRows from "./Rows";
import useSpaceDetailsIdpGroups from "./useSpaceDetailsIdpGroups";
import useSpaceDetailsIdpGroupsTableStore from "./useStore";

type SpaceDetailsIdpGroupsProps = {
  spaceId: string;
};

const TABLE_ARIA_LABEL = "Space IdP groups table";

const SpaceDetailsIdpGroups = ({ spaceId }: SpaceDetailsIdpGroupsProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_IDP_GROUPS_LIMIT_STORAGE_KEY,
    SPACE_IDP_GROUPS_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_IDP_GROUPS_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;
  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(
        searchParams,
        SPACE_IDP_GROUPS_INITIAL_ORDER_BY.field,
        SearchQueryOrderDirection.Asc
      )[0] ?? SPACE_IDP_GROUPS_INITIAL_ORDER_BY,
    [searchParams]
  );

  const columnIds = useSpaceDetailsIdpGroupsTableStore((state) => state.columnsOrder.visible);

  const {
    loadIdpGroups,
    error,
    idpGroups,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsIdpGroups({ spaceId, orderBy, first, fullTextSearch });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadIdpGroups,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: idpGroups.length,
    initialLimit: SPACE_IDP_GROUPS_INITIAL_LIMIT,
    storageKey: SPACE_IDP_GROUPS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="IdP Groups" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search IdP groups"
            filtersOrderSettingsKey={SPACE_IDP_GROUPS_FILTERS_ORDER_KEY}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <SpaceDetailsLoginPolicyCallout />

      <ListContentWrapper>
        {!idpGroups.length && !fullTextSearch && (
          <EmptyState
            title="This space has no IdP groups yet"
            icon={EmptystateShareColored}
            caption="IdP groups with access to this space will show up here."
          />
        )}

        {!idpGroups.length && !!fullTextSearch && (
          <EmptyState
            title="No IdP groups found"
            icon={EmptystateSearchNoResultsColored}
            caption="No IdP groups match your search."
          />
        )}

        {!!idpGroups.length && (
          <>
            <TableWrapper ref={tableRef} hasPagination>
              <Table ariaLabel={TABLE_ARIA_LABEL}>
                <TableSimpleHead
                  store={useSpaceDetailsIdpGroupsTableStore}
                  ariaLabel={TABLE_ARIA_LABEL}
                  tableRef={tableRef}
                  sortConfig={SPACE_IDP_GROUPS_SORT_CONFIG}
                  filtersStorageKey={SPACE_IDP_GROUPS_FILTERS_ORDER_KEY}
                  hideSettings
                  stickyRight={() => false}
                />
                {loadingContent && (
                  <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />
                )}
                {!loadingContent && (
                  <TableBody>
                    <SpaceDetailsIdpGroupsRows
                      data={idpGroups}
                      store={useSpaceDetailsIdpGroupsTableStore}
                      tableRef={tableRef}
                    />
                  </TableBody>
                )}
              </Table>
            </TableWrapper>
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsIdpGroups;
